// Star reviews for the library.
//
// Separate from the existing like/dislike community votes: this is a 1-5 star rating,
// signed-in only to cast (so one account = one rating), which also drives a "Top rated"
// ranking. The public aggregate ({ bookId: {avg, count} }) is readable by everyone; a
// signed-in user's own ratings come back from /account/reviews so their stars show
// filled on return.

import { authedFetch, getSession, onAccountChange } from "./account.js";

let agg = null;           // { bookId: {avg, count} }
let mine = null;          // { bookId: stars }
let aggPromise = null;
const readyFns = new Set();

export function onReviewsReady(fn) { readyFns.add(fn); return () => readyFns.delete(fn); }
function fireReady() { for (const fn of readyFns) { try { fn(); } catch (e) { void e; } } }

// Load the public aggregate once (cached for the session).
export function ensureAggregate() {
	if (agg) return Promise.resolve(agg);
	if (aggPromise) return aggPromise;
	aggPromise = fetch("/reviews", { cache: "no-store" })
		.then((r) => (r.ok ? r.json() : {}))
		.then((j) => { agg = j || {}; fireReady(); return agg; })
		.catch(() => { agg = {}; return agg; });
	return aggPromise;
}
// Load this user's own ratings if signed in.
export async function ensureMine() {
	if (!getSession()) { mine = {}; return mine; }
	if (mine) return mine;
	try {
		const res = await authedFetch("/account/reviews");
		mine = res.ok ? (await res.json()).reviews || {} : {};
	} catch (e) { void e; mine = {}; }
	fireReady();
	return mine;
}

export function aggFor(bookId) { return (agg && agg[bookId]) || null; }
export function mineFor(bookId) { return (mine && mine[bookId]) || 0; }
export function isSignedIn() { return !!getSession(); }

// Cast or change a rating. Optimistically updates the local caches so the UI can
// repaint immediately, then reconciles with the server's response.
export async function rate(bookId, stars) {
	const id = String(bookId);
	const prev = mineFor(id);
	// optimistic aggregate update
	if (!agg) agg = {};
	const a = agg[id] || { avg: 0, count: 0 };
	let sum = a.avg * a.count;
	if (prev) sum -= prev; else a.count += 1;
	if (stars) sum += stars; else a.count -= 1;
	a.avg = a.count > 0 ? Math.round((sum / a.count) * 10) / 10 : 0;
	if (a.count > 0) agg[id] = a; else delete agg[id];
	if (!mine) mine = {};
	if (stars) mine[id] = stars; else delete mine[id];

	try {
		const res = await authedFetch("/reviews/" + encodeURIComponent(id), {
			method: "POST", headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ stars }),
		});
		if (!res.ok) throw new Error("rate failed");
		// Refresh the true aggregate in the background (cheap, cached 30s server-side).
		agg = null; aggPromise = null; ensureAggregate();
		return true;
	} catch (e) {
		void e;
		return false;
	}
}

// When sign-in state changes, drop the per-user cache so it reloads for the new user.
onAccountChange(() => { mine = null; ensureMine(); });
